import { configureStore, combineReducers, AnyAction } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { restaurantsSlice } from '../../features/manager/restaurantList/restaurantSlice';
import { createRestaurantSlice } from '../../features/manager/restaurantList/createRestaurantSlice';
import { updateRestaurantSlice } from '../../features/manager/restaurantList/updateRestaurantSlice';
import { kitchenListSlice } from '../../features/manager/kitchens/kitchenListSlice';
import { createKitchenSlice } from '../../features/manager/kitchens/createKitchenSlice';
import { mealItemListSlice } from '../../features/manager/mealItems/mealItemListSlice';
import { createMealItemSlice } from '../../features/manager/mealItems/createMealItemSlice';
import { customerRestaurantSlice } from '../../features/customer/restaurant/customerRestaurantSlice';
import { cartSlice } from '../../features/customer/cart/cartSlice';
import { accountSlice } from '../../features/account/accountSlice';
import { languageSlice } from '../layout/languageSlice';
import { kitchenDetailsSlice } from '../../features/manager/kitchens/kitchenDetailsSlice';
import { kitchenOrdersSlice } from '../../features/manager/kitchens/orders/kitchenOrdersSlice';
import { customerOrdersSlice } from '../../features/customer/orders/customerOrdersSlice';
import { orderListSlice } from '../../features/manager/orders/orderListSlice';
import { restaurantUserListSlice } from '../../features/manager/users/restaurantUserListSlice';
import { kitchenStaffRestaurantSlice } from '../../features/kitchenStaff/restaurantList/kitchenStaffRestaurantSlice';
import { createRestaurantUserSlice } from '../../features/manager/users/createRestaurantUserSlice';
import { drawerMenuSlice } from '../../features/common/drawerMenu/drawerMenuSlice';
import { customerHomeSlice } from '../../features/customer/home/customerHomeSlice';
import appReducer from './appSlice';
import { mealItemDetailsSlice } from '../../features/manager/mealItems/mealItemDetailsSlice';
import { updateMealItemSlice } from '../../features/manager/mealItems/updateMealItemSlice';
import { orderDetailsSlice } from '../../features/manager/orders/orderDetailsSlice';
import { mealItemCustomizationSlice } from '../../features/manager/mealItems/customizations/mealItemCustomizationSlice';
import { tableSlice } from '../../features/manager/tables/store/tableSlice';
import { menuSlice } from '../../features/manager/menus/store/menuSlice';
import dialogReducer from './dialogSlice';
import { waiterRestaurantSlice } from '../../features/restaurantWaiter/restaurants/waiterRestaurantListSlice';
import { waiterTableSlice } from '../../features/restaurantWaiter/tables/waiterTableListSlice';
import { waiterOrderSlice } from '../../features/restaurantWaiter/orders/waiterOrderSlice';
import kitchenMealItemsReducer from '../../features/kitchenStaff/kitchenMealItems/kitchenMealItemsSlice';
import billerHomeReducer from '../../features/biller/home/billerHomeSlice';
import billerOrdersReducer from '../../features/biller/billerOrdersSlice';
import { taxSlice } from '../../features/manager/taxes/taxSlice';

import { drawerRestaurantSlice } from '../../features/common/drawerMenu/drawerRestaurantSlice';

import { restaurantsCacheSlice } from '../../features/customer/restaurant/restaurantsCacheSlice';

// Create a root reducer (not exported)
const rootReducer = combineReducers({
  drawerMenu: drawerMenuSlice.reducer,
  drawerRestaurant: drawerRestaurantSlice.reducer,
  restaurants: restaurantsSlice.reducer,
  kitchenStaffRestaurants: kitchenStaffRestaurantSlice.reducer,
  waiterRestaurants: waiterRestaurantSlice.reducer,
  waiterTables: waiterTableSlice.reducer,
  createRestaurant: createRestaurantSlice.reducer,
  updateRestaurant: updateRestaurantSlice.reducer,
  kitchens: kitchenListSlice.reducer,
  kitchen: kitchenDetailsSlice.reducer,
  kitchenOrders: kitchenOrdersSlice.reducer,
  createKitchen: createKitchenSlice.reducer,
  menus: menuSlice.reducer,
  mealItems: mealItemListSlice.reducer,
  mealItemCustomizations: mealItemCustomizationSlice.reducer,
  createMealItem: createMealItemSlice.reducer,
  customerRestaurants: restaurantsCacheSlice.reducer,
  customerRestaurant: customerRestaurantSlice.reducer,
  customerHome: customerHomeSlice.reducer,
  cart: cartSlice.reducer,
  account: accountSlice.reducer,
  language: languageSlice.reducer,
  orders: orderListSlice.reducer,
  customerOrders: customerOrdersSlice.reducer,
  restaurantUsers: restaurantUserListSlice.reducer,
  createRestaurantUser: createRestaurantUserSlice.reducer,
  app: appReducer,
  kitchenDetails: kitchenDetailsSlice.reducer,
  mealItemDetails: mealItemDetailsSlice.reducer,
  updateMealItem: updateMealItemSlice.reducer,
  orderDetails: orderDetailsSlice.reducer,
  tables: tableSlice.reducer,
  dialog: dialogReducer,
  waiterOrder: waiterOrderSlice.reducer,
  kitchenMealItems: kitchenMealItemsReducer,
  billerRestaurants: billerHomeReducer,
  billerOrders: billerOrdersReducer,
  taxes: taxSlice.reducer,
});

// Explicitly define RootState to avoid type leaks from slices with unnameable types
export interface RootState {
  drawerMenu: ReturnType<typeof drawerMenuSlice.reducer>;
  drawerRestaurant: ReturnType<typeof drawerRestaurantSlice.reducer>;
  restaurants: ReturnType<typeof restaurantsSlice.reducer>;
  kitchenStaffRestaurants: ReturnType<
    typeof kitchenStaffRestaurantSlice.reducer
  >;
  waiterRestaurants: ReturnType<typeof waiterRestaurantSlice.reducer>;
  waiterTables: ReturnType<typeof waiterTableSlice.reducer>;
  createRestaurant: ReturnType<typeof createRestaurantSlice.reducer>;
  updateRestaurant: ReturnType<typeof updateRestaurantSlice.reducer>;
  kitchens: ReturnType<typeof kitchenListSlice.reducer>;
  kitchen: ReturnType<typeof kitchenDetailsSlice.reducer>;
  kitchenOrders: ReturnType<typeof kitchenOrdersSlice.reducer>;
  createKitchen: ReturnType<typeof createKitchenSlice.reducer>;
  menus: ReturnType<typeof menuSlice.reducer>;
  mealItems: ReturnType<typeof mealItemListSlice.reducer>;
  mealItemCustomizations: ReturnType<typeof mealItemCustomizationSlice.reducer>;
  createMealItem: ReturnType<typeof createMealItemSlice.reducer>;
  customerRestaurants: ReturnType<typeof restaurantsCacheSlice.reducer>;
  customerRestaurant: ReturnType<typeof customerRestaurantSlice.reducer>;
  customerHome: ReturnType<typeof customerHomeSlice.reducer>;
  cart: ReturnType<typeof cartSlice.reducer>;
  account: ReturnType<typeof accountSlice.reducer>;
  language: ReturnType<typeof languageSlice.reducer>;
  orders: ReturnType<typeof orderListSlice.reducer>;
  customerOrders: ReturnType<typeof customerOrdersSlice.reducer>;
  restaurantUsers: ReturnType<typeof restaurantUserListSlice.reducer>;
  createRestaurantUser: ReturnType<typeof createRestaurantUserSlice.reducer>;
  app: ReturnType<typeof appReducer>;
  kitchenDetails: ReturnType<typeof kitchenDetailsSlice.reducer>;
  mealItemDetails: ReturnType<typeof mealItemDetailsSlice.reducer>;
  updateMealItem: ReturnType<typeof updateMealItemSlice.reducer>;
  orderDetails: ReturnType<typeof orderDetailsSlice.reducer>;
  tables: ReturnType<typeof tableSlice.reducer>;
  dialog: ReturnType<typeof dialogReducer>;
  waiterOrder: ReturnType<typeof waiterOrderSlice.reducer>;
  kitchenMealItems: ReturnType<typeof kitchenMealItemsReducer>;
  billerRestaurants: ReturnType<typeof billerHomeReducer>;
  billerOrders: ReturnType<typeof billerOrdersReducer>;
  taxes: ReturnType<typeof taxSlice.reducer>;
}

// Create app reducer with reset functionality
const appRootReducer = (
  state: RootState | undefined,
  action: AnyAction
): RootState => {
  // First let the normal reducers process the action (including navigation)
  const newState = rootReducer(state, action);

  // Then apply our state reset logic AFTER reducers have processed the action
  if (action.type === 'account/logout') {
    // Get initial state from the root reducer
    const initialState = rootReducer(undefined, { type: '@@INIT' });

    // Return state with all slices reset to initial values except account
    // which was already handled by the account reducer
    return {
      ...initialState,
      account: newState.account,
    };
  }

  return newState;
};

export const store = configureStore({
  reducer: appRootReducer,
});

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
